import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Alert,
    CircularProgress,
    Box,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { expenseService } from "../../services/expenseService";
import {
    Expense,
    ExpenseCreate,
    ExpenseUpdate,
    ExpenseCategory,
    PaymentMethod,
} from "../../types";
import { AccountPicker } from "../accounts/AccountPicker";
import {
    EXPENSE_PAYMENT_METHODS,
    PAYMENT_METHOD_LABELS,
} from "../../utils/paymentMethods";

interface ExpenseFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    expense?: Expense | null;
    mode: "create" | "edit";
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
    open,
    onClose,
    onSuccess,
    expense,
    mode,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);

    const [formData, setFormData] = useState<{
        category_id: string;
        amount: string;
        expense_date: Date;
        payment_method: PaymentMethod;
        vendor_name: string;
        reference_number: string;
        notes: string;
        account_id: string | null;
    }>({
        category_id: "",
        amount: "",
        expense_date: new Date(),
        payment_method: "CASH",
        vendor_name: "",
        reference_number: "",
        notes: "",
        account_id: null,
    });

    useEffect(() => {
        if (open) {
            fetchCategories();
            if (mode === "edit" && expense) {
                setFormData({
                    category_id: expense.category.category_id,
                    amount: expense.amount.toString(),
                    expense_date: new Date(expense.expense_date),
                    payment_method: expense.payment_method,
                    vendor_name: expense.vendor_name || "",
                    reference_number: expense.reference_number || "",
                    notes: expense.notes || "",
                    account_id: expense.account_id || null,
                });
            } else {
                setFormData({
                    category_id: "",
                    amount: "",
                    expense_date: new Date(),
                    payment_method: "CASH",
                    vendor_name: "",
                    reference_number: "",
                    notes: "",
                    account_id: null,
                });
            }
            setError("");
        }
    }, [open, mode, expense]);

    const fetchCategories = async () => {
        try {
            setLoadingCategories(true);
            const activeCategories = await expenseService.getActiveCategories();
            setCategories(activeCategories);
        } catch (err: any) {
            console.error("Failed to fetch categories:", err);
            setError("Failed to load categories");
        } finally {
            setLoadingCategories(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!formData.category_id) {
            setError("Please select a category");
            return;
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            setError("Please enter a valid amount");
            return;
        }

        try {
            setLoading(true);

            const expenseData: ExpenseCreate | ExpenseUpdate = {
                category_id: formData.category_id,
                amount: parseFloat(formData.amount),
                expense_date: formData.expense_date.toISOString(),
                payment_method: formData.payment_method,
                vendor_name: formData.vendor_name || undefined,
                reference_number: formData.reference_number || undefined,
                notes: formData.notes || undefined,
                // null (not undefined) so editing can explicitly clear a
                // previously-tagged account — see AccountPicker's docstring.
                account_id: formData.account_id,
            };

            if (mode === "create") {
                await expenseService.createExpense(
                    expenseData as ExpenseCreate,
                );
            } else if (expense) {
                await expenseService.updateExpense(
                    expense.expense_id,
                    expenseData as ExpenseUpdate,
                );
            }

            onSuccess();
        } catch (err: any) {
            console.error("Failed to save expense:", err);
            setError(
                err.response?.data?.detail ||
                    err.message ||
                    "Failed to save expense",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {mode === "create" ? "Add New Expense" : "Edit Expense"}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loadingCategories ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                p: 3,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: 1,
                            }}
                        >
                            <TextField
                                select
                                label="Category"
                                value={formData.category_id}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        category_id: e.target.value,
                                    })
                                }
                                required
                                fullWidth
                            >
                                {categories.map((category) => (
                                    <MenuItem
                                        key={category.category_id}
                                        value={category.category_id}
                                    >
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                label="Amount"
                                type="number"
                                value={formData.amount}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        amount: e.target.value,
                                    })
                                }
                                required
                                fullWidth
                                inputProps={{ min: 0, step: 0.01 }}
                            />

                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                    label="Expense Date"
                                    value={formData.expense_date}
                                    onChange={(newValue) =>
                                        setFormData({
                                            ...formData,
                                            expense_date:
                                                newValue || new Date(),
                                        })
                                    }
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            required: true,
                                        },
                                    }}
                                />
                            </LocalizationProvider>

                            <TextField
                                select
                                label="Payment Method"
                                value={formData.payment_method}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        payment_method: e.target
                                            .value as PaymentMethod,
                                    })
                                }
                                required
                                fullWidth
                            >
                                {EXPENSE_PAYMENT_METHODS.map((method) => (
                                    <MenuItem key={method} value={method}>
                                        {PAYMENT_METHOD_LABELS[method]}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <AccountPicker
                                value={formData.account_id}
                                onChange={(account_id) =>
                                    setFormData({ ...formData, account_id })
                                }
                                helperText="Which cash/bank account this was paid from"
                            />

                            <TextField
                                label="Vendor Name"
                                value={formData.vendor_name}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        vendor_name: e.target.value,
                                    })
                                }
                                fullWidth
                            />

                            <TextField
                                label="Reference Number"
                                value={formData.reference_number}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        reference_number: e.target.value,
                                    })
                                }
                                fullWidth
                            />

                            <TextField
                                label="Notes"
                                value={formData.notes}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        notes: e.target.value,
                                    })
                                }
                                multiline
                                rows={3}
                                fullWidth
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || loadingCategories}
                    >
                        {loading ? (
                            <CircularProgress size={24} />
                        ) : mode === "create" ? (
                            "Add"
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};
